'use client';

import {
  SquishEngine,
  resolveShape,
  resolveExpression,
  type SquishJson,
} from '@usespaceui/squishmoji';
import { useEffect, type RefObject } from 'react';

/** Keep the seeded paint while morphing the body, clipping paths and fitted eyes together. */
export function useSubAgentMorph(
  ref: RefObject<HTMLSpanElement | null>,
  avatar: SquishJson,
  running: boolean
) {
  useEffect(() => {
    const host = ref.current;
    if (!host || !running) return;
    const bodies = [...host.querySelectorAll('path')].filter(
      (path) => path.getAttribute('d') === avatar.layout.body
    );
    const eyes = [...host.querySelectorAll('[data-sq-eye]')];
    const originals = [...bodies, ...eyes].map((node) => ({
      node,
      d: node.getAttribute('d'),
      transform: node.getAttribute('transform'),
    }));
    // A shared page clock keeps duplicate views of this worker in the same pose.
    const currentIndex = Math.max(
      0,
      Math.floor((performance.now() - 600) / 2400) + 1
    );
    let index = Math.max(0, currentIndex - 1);
    const previousSeed = `${avatar.seed}:${index}`;
    const engine = new SquishEngine(avatar.seed, {
      shape: index ? resolveShape(previousSeed, 'all') : avatar.shape,
      expression: index
        ? resolveExpression(previousSeed, 'all')
        : avatar.expression,
      backgroundStyle: avatar.backgroundStyle,
    });
    let frame = 0;
    let lastDraw = -Infinity;
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      if (now - lastDraw < 32) return;
      lastDraw = now;
      const time = now / 1000;
      const nextIndex = Math.max(0, Math.floor((now - 600) / 2400) + 1);
      if (nextIndex !== index) {
        index = nextIndex;
        const seed = `${avatar.seed}:${index}`;
        const changeAt = (600 + (index - 1) * 2400) / 1000;
        engine.setShape(resolveShape(seed, 'all'), changeAt, 0.9);
        engine.setExpression(resolveExpression(seed, 'all'), changeAt, 0.6);
      }
      const layout = engine.layout(time);
      bodies.forEach((path) => path.setAttribute('d', layout.body));
      layout.eyes.forEach((eye, i) => {
        const node = eyes[i];
        if (!node) return;
        const x = eye.width / 2;
        const y = eye.height / 2;
        const r = Math.min(x, y);
        node.setAttribute(
          'd',
          `M ${-x + r} ${-y} H ${x - r} Q ${x} ${-y} ${x} ${-y + r} V ${y - r} Q ${x} ${y} ${x - r} ${y} H ${-x + r} Q ${-x} ${y} ${-x} ${y - r} V ${-y + r} Q ${-x} ${-y} ${-x + r} ${-y} Z`
        );
        node.setAttribute('transform', `matrix(${eye.matrix.join(' ')})`);
      });
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      originals.forEach(({ node, d, transform }) => {
        if (d !== null) node.setAttribute('d', d);
        if (transform !== null) node.setAttribute('transform', transform);
      });
    };
  }, [ref, avatar, running]);
}
